import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import PetSearch from '../../PetSearch';
import Feedings from './Feedings';
import styles from './FeedingsAndMedications.module.scss';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import Button from 'components/UI/V3/Button/Button';
import Medications from './Medications';
import { noop } from 'lodash';
import { Feeding, FeedingScheduleType, Medication, versionPetAndServices } from 'model/NewMultipleBooking';
import useNonInitialEffect from '@versiondos/hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { NewBookingsStatus } from '@spike/new-bookings-action';
import { OrdersStatus } from '@spike/orders-action';
import moment, { Moment } from 'moment-timezone';
import { useMarketplace, useMasterData, useTimeZone } from 'hooks';
import {
    getTodayClosingTime,
    getTodayOpeningTime
} from 'components/CreateBooking/Memberships/utils/membershipItemFlow';
import { createTaskDate } from 'utils/dateTimeUtils';
import { getFeedingSlotTimes } from 'utils/feedingSlotTimes';
import { getMarketplaceDisabledFeedingMoments } from 'utils/feedingMomentSettings';
import { DaySchedule } from './UI/MedicalSchedule/MedicalSchedule';
import type Marketplace from '@spike/marketplace-model';
import {
    getMaxDecimalsForUnit as getFoodMaxDecimals,
    isValidDecimalPlaces as isValidFoodDecimalPlaces
} from './UI/FoodQuantity/foodQuantityUnitsConfig';
import {
    getMaxDecimalsForUnit as getMedicationMaxDecimals,
    isValidDecimalPlaces as isValidMedicationDecimalPlaces
} from './UI/MedicationDosage/medicationDosageUnitsConfig';

interface Props {
    parentId: number;
    petId: number;
    feedings: Array<Feeding>;
    medications: Array<Medication>;
    from: Moment;
    to: Moment;
    flow: 'boarding' | 'daycare';
    versionPetAndServices?: versionPetAndServices;
    onBack: () => void;
    onSave: (feedings: Array<Feeding>, medications: Array<Medication>) => void;
}

const resolveDaycareMedicationWindow = (
    from: Moment,
    to: Moment | undefined,
    marketplace: Marketplace
): { from: Moment; to: Moment } => {
    const opening = getTodayOpeningTime(from, marketplace);
    const closing = moment(getTodayClosingTime(from, marketplace)).tz(marketplace.schedule.timeZone);

    if (!from?.isValid()) {
        return { from: opening, to: closing };
    }

    const appointmentTo = to?.isValid() ? to : closing;
    const appointmentMinutes = appointmentTo.diff(from, 'minutes');

    if (appointmentMinutes < 60) {
        return { from: opening, to: closing };
    }

    return {
        from: from.isAfter(opening) ? from.clone() : opening.clone(),
        to: appointmentTo.isBefore(closing) ? appointmentTo.clone() : closing.clone()
    };
};

export const FeedingsAndMedications: FunctionComponent<Props> = props => {
    const marketplace = useMarketplace();
    const masterData = useMasterData();
    const timezone = useTimeZone();
    const [feedings, setFeedings] = useState<Array<Feeding>>(props.feedings);
    const [medications, setMedications] = useState<Array<Medication>>(props.medications);
    const [isFeedingActive, setIsFeedingActive] = useState(props.feedings.length > 0);
    const [isMedicationActive, setIsMedicationActive] = useState(props.medications.length > 0);
    const [disabledSave, setDisabledSave] = useState(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [medicationAlerts, setMedicationAlerts] = useState<boolean>(false);

    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);

    const saveButtonLoading =
        saving ||
        newBookingsStatus === NewBookingsStatus.SavingMultiple ||
        newBookingsStatus === NewBookingsStatus.SavingMultipleBookingForDeposit ||
        ordersStatus === OrdersStatus.Getting ||
        ordersStatus === OrdersStatus.SavingOrderRedeem;

    const { morning, afternoon, evening } = getFeedingSlotTimes(marketplace, masterData, { fallbackToDefaults: true });

    const morningFeedingDate =
        props.flow === 'daycare' && morning ? createTaskDate(props.from, morning, timezone) : undefined;
    const afternoonFeedingDate =
        props.flow === 'daycare' && afternoon ? createTaskDate(props.from, afternoon, timezone) : undefined;
    const eveningFeedingDate =
        props.flow === 'daycare' && evening ? createTaskDate(props.from, evening, timezone) : undefined;

    const medicationWindow = useMemo(() => {
        if (props.flow !== 'daycare') {
            return { from: props.from, to: props.to };
        }
        return resolveDaycareMedicationWindow(props.from, props.to, marketplace);
    }, [props.flow, props.from, props.to, marketplace]);

    const getFeedingSelected = (from?: Moment | undefined, to?: Moment | undefined) => {
        if (
            morningFeedingDate?.isValid() &&
            afternoonFeedingDate?.isValid() &&
            eveningFeedingDate?.isValid() &&
            from?.isValid() &&
            to?.isValid()
        ) {
            if (from.isSameOrAfter(eveningFeedingDate)) {
                return 3;
            }
            if (from.isBefore(afternoonFeedingDate) && to.isSameOrAfter(morningFeedingDate)) {
                return 1;
            }
            if (from.isBefore(eveningFeedingDate) && to.isSameOrAfter(afternoonFeedingDate)) {
                return 2;
            }
            if (from.isBefore(eveningFeedingDate) && to.isSameOrAfter(eveningFeedingDate)) {
                return 3;
            }
        }
        return undefined;
    };

    const getFeedingsDisabled = (from?: Moment, to?: Moment): number[] => {
        const feedingsDisabled: Array<number> = [];
        if (
            !from?.isValid() ||
            !to?.isValid() ||
            !morningFeedingDate?.isValid() ||
            !afternoonFeedingDate?.isValid() ||
            !eveningFeedingDate?.isValid()
        ) {
            return feedingsDisabled;
        }

        const morningActive = from.isBefore(afternoonFeedingDate) && to.isSameOrAfter(morningFeedingDate);
        const afternoonActive = from.isBefore(eveningFeedingDate) && to.isSameOrAfter(afternoonFeedingDate);
        const eveningActive =
            from.isSameOrAfter(eveningFeedingDate) ||
            (from.isBefore(eveningFeedingDate) && to.isSameOrAfter(eveningFeedingDate));

        if (!morningActive) feedingsDisabled.push(1);
        if (!afternoonActive) feedingsDisabled.push(2);
        if (!eveningActive) feedingsDisabled.push(3);

        return feedingsDisabled;
    };

    const marketplaceDisabled = getMarketplaceDisabledFeedingMoments(marketplace);

    const disabledMoments =
        props.flow === 'daycare'
            ? [...getFeedingsDisabled(props.from, props.to), ...marketplaceDisabled]
            : marketplaceDisabled;

    const getNextScheduleTime = (): string => {
        if (props.from && props.to) {
            const allSchedules = medications.flatMap(med => med.schedule);
            if (allSchedules.length > 0) {
                const scheduleMinutes = allSchedules.map(time => {
                    const [timePart, period] = time.split(/(am|pm)/i);
                    const [hours, minutes] = timePart.split(':').map(Number);
                    let hour24 = hours;
                    if (period.toLowerCase() === 'pm' && hours !== 12) {
                        hour24 += 12;
                    } else if (period.toLowerCase() === 'am' && hours === 12) {
                        hour24 = 0;
                    }
                    return hour24 * 60 + minutes;
                });

                const maxMinutes = Math.max(...scheduleMinutes);
                const nextMinutes = maxMinutes + 15;

                const nextHour = Math.floor(nextMinutes / 60) % 24;
                const nextMinute = nextMinutes % 60;

                const nextDate = new Date();
                nextDate.setHours(nextHour, nextMinute, 0, 0);

                return nextDate
                    .toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    })
                    .replace(' ', '')
                    .toLowerCase();
            }
            return props.from.format('h:mmA').toLowerCase();
        }
        return '8:00am';
    };

    const getNextSpecificScheduleTime = (): Array<DaySchedule> => {
        if (props.from && props.to) {
            const specificSchedule: Array<DaySchedule> = [];
            const current = props.from.clone();
            while (current.isSameOrBefore(props.to, 'day')) {
                specificSchedule.push({
                    date: current.clone(),
                    enabled: false,
                    doses: [getNextScheduleTime()]
                });
                current.add(1, 'day');
            }
            return specificSchedule;
        }
        return [];
    };

    const getNewMedication = (): Medication => ({
        name: '',
        methodType: undefined,
        dosage: { quantity: undefined, unit: undefined },
        instructions: '',
        schedule: [getNextScheduleTime()],
        specificSchedule: []
    });

    const newFeeding: Feeding = {
        momentTypes:
            getFeedingSelected(props.from, props.to) && props.flow === 'daycare'
                ? [getFeedingSelected(props.from, props.to)!]
                : [],
        foodProvisionType: 1,
        foodType: undefined,
        quantity: { quantity: undefined, unit: undefined },
        brand: '',
        instructions: ''
    };

    const handleAddFeeding = () => {
        setFeedings([...feedings, newFeeding]);
    };

    const handleAddMedication = () => {
        setMedications([...medications, getNewMedication()]);
    };

    const handleUpdateFeeding = (index: number, feeding: Feeding) => {
        const updatedFeedings = [...feedings];
        updatedFeedings[index] = feeding;
        setFeedings(updatedFeedings);
    };

    const handleUpdateMedication = (index: number, medication: Medication) => {
        const updatedMedications = [...medications];
        updatedMedications[index] = medication;
        setMedications(updatedMedications);
    };

    const handleSetMedicationType = (index: number, type: string) => {
        const updatedMedications = [...medications];
        if (type === 'daily') {
            updatedMedications[index] = {
                ...updatedMedications[index],
                specificSchedule: [],
                schedule: [getNextScheduleTime()]
            };
        } else {
            updatedMedications[index] = {
                ...updatedMedications[index],
                schedule: [],
                specificSchedule: getNextSpecificScheduleTime()
            };
        }
        setMedications(updatedMedications);
    };

    const handleDeleteFeeding = (index: number) => {
        const updatedFeedings = feedings.filter((_, i) => i !== index);
        if (updatedFeedings.length === 0) {
            setIsFeedingActive(false);
        } else {
            setFeedings(updatedFeedings);
        }
    };

    const handleDeleteMedication = (index: number) => {
        const updatedMedications = medications.filter((_, i) => i !== index);
        if (updatedMedications.length === 0) {
            setIsMedicationActive(false);
        } else {
            setMedications(updatedMedications);
        }
    };

    const handleActivateMedication = (isActive: boolean) => {
        setIsMedicationActive(isActive);
        setMedications([getNewMedication()]);
    };

    const handleActivateFeeding = (isActive: boolean) => {
        setIsFeedingActive(isActive);
        setFeedings([newFeeding]);
    };

    const normalizeFeedingsForSave = (feedingsToSave: Array<Feeding>): Array<Feeding> =>
        feedingsToSave.map(f => {
            const scheduleType: FeedingScheduleType = f.feedingScheduleType ?? 'feedingMoment';
            if (scheduleType === 'feedingMoment') {
                const { specificMomentTimes: _, ...rest } = f;
                return { ...rest, specificMomentTimes: undefined };
            }
            const momentTypesForSpecific =
                f.momentTypes && f.momentTypes.length > 0
                    ? f.momentTypes
                    : Object.keys(f.specificMomentTimes || {}).map(Number);
            return {
                ...f,
                momentTypes: momentTypesForSpecific,
                feedingScheduleType: 'specificTime' as const,
                specificMomentTimes:
                    f.specificMomentTimes && Object.keys(f.specificMomentTimes).length > 0
                        ? f.specificMomentTimes
                        : undefined
            };
        });

    const handleSave = () => {
        const feedingsToSave = normalizeFeedingsForSave(feedings);
        if (!isFeedingActive && !isMedicationActive) {
            props.onSave([], []);
        } else if (!isFeedingActive && isMedicationActive) {
            props.onSave([], medications);
        } else if (isFeedingActive && !isMedicationActive) {
            props.onSave(feedingsToSave, []);
        } else {
            props.onSave(feedingsToSave, medications);
        }
    };

    const handleBack = () => {
        const feedingsToSave = normalizeFeedingsForSave(feedings);
        if (!isFeedingActive && !isMedicationActive) {
            props.onSave([], []);
        } else if (!isFeedingActive && isMedicationActive) {
            props.onSave([], medications);
        } else if (isFeedingActive && !isMedicationActive) {
            props.onSave(feedingsToSave, []);
        } else {
            props.onSave(feedingsToSave, medications);
        }
        props.onBack();
    };

    const getSpecificScheduleEnabledCount = () => {
        return medications.reduce((acc, m) => {
            return acc + m.specificSchedule.filter(s => s.enabled).length;
        }, 0);
    };

    const feedingsOrMedicationsIncomplete = () => {
        const foodUnits = masterData.foodQuantityUnits;
        const medicationUnits = masterData.medicationDosageUnits;

        const feedingIncomplete = feedings.some(feeding => {
            const hasQuantity = feeding.quantity.quantity !== undefined && feeding.quantity.quantity !== null;
            const unitId = feeding.quantity.unit;
            const hasUnit =
                unitId !== undefined && unitId !== null && unitId !== 0 && foodUnits.some(u => u.id === unitId);

            if (
                feeding.foodType === undefined ||
                feeding.momentTypes.length === 0 ||
                !hasQuantity ||
                (feeding.quantity.quantity ?? 0) <= 0 ||
                !hasUnit
            ) {
                return true;
            }

            const unitName = foodUnits.find(u => u.id === feeding.quantity.unit)?.name ?? '';
            const maxDecimals = getFoodMaxDecimals(unitName);
            if (maxDecimals !== undefined && !isValidFoodDecimalPlaces(feeding.quantity.quantity!, maxDecimals)) {
                return true;
            }
            return false;
        });

        const feedingsIncomplete = isFeedingActive && feedingIncomplete;

        const medicationsIncomplete =
            isMedicationActive &&
            medications.length > 0 &&
            medications.some(medication => {
                const hasDosageQty = medication.dosage.quantity !== undefined && medication.dosage.quantity !== null;
                const dosageUnitId = medication.dosage.unit;
                const hasDosageUnit =
                    dosageUnitId !== undefined &&
                    dosageUnitId !== null &&
                    dosageUnitId !== 0 &&
                    medicationUnits.some(u => u.id === dosageUnitId);

                if (
                    medication.name === '' ||
                    medication.methodType === undefined ||
                    !hasDosageQty ||
                    (medication.dosage.quantity ?? 0) <= 0 ||
                    !hasDosageUnit ||
                    (medication.schedule.length === 0 &&
                        medication.specificSchedule.length > 0 &&
                        medication.specificSchedule.every(day => !day.enabled))
                ) {
                    return true;
                }

                const unitName = medicationUnits.find(u => u.id === medication.dosage.unit)?.name ?? '';
                const maxDecimals = getMedicationMaxDecimals(unitName);
                if (
                    maxDecimals !== undefined &&
                    !isValidMedicationDecimalPlaces(medication.dosage.quantity!, maxDecimals)
                ) {
                    return true;
                }
                return false;
            });

        return feedingsIncomplete || medicationsIncomplete;
    };

    const specificScheduleEnabledCount = getSpecificScheduleEnabledCount();

    useEffect(() => {
        if (
            props.versionPetAndServices === versionPetAndServices.add ||
            props.versionPetAndServices === versionPetAndServices.another ||
            (props.versionPetAndServices === versionPetAndServices.edit && feedings.length === 0)
        ) {
            handleAddFeeding();
        }
        if (
            props.versionPetAndServices === versionPetAndServices.add ||
            props.versionPetAndServices === versionPetAndServices.another ||
            (props.versionPetAndServices === versionPetAndServices.edit && medications.length === 0)
        ) {
            handleAddMedication();
        }
    }, []);

    useEffect(() => {
        const isIncomplete = feedingsOrMedicationsIncomplete();
        setDisabledSave(isIncomplete || medicationAlerts);
    }, [feedings, medications, isMedicationActive, isFeedingActive, specificScheduleEnabledCount, medicationAlerts]);

    useEffect(() => {
        if (props.flow === 'boarding' && medications.length > 0) {
            const updatedMedications = medications.map(medication => {
                if (medication.specificSchedule && medication.specificSchedule.length > 0) {
                    const filteredSchedule = medication.specificSchedule.filter(daySchedule => {
                        return (
                            daySchedule.date.isSameOrAfter(props.from, 'day') &&
                            daySchedule.date.isSameOrBefore(props.to, 'day')
                        );
                    });

                    if (filteredSchedule.length !== medication.specificSchedule.length) {
                        return {
                            ...medication,
                            specificSchedule: filteredSchedule
                        };
                    }

                    const existingDates = new Set(medication.specificSchedule.map(ds => ds.date.format('YYYY-MM-DD')));
                    const current = props.from.clone();
                    const newDays: DaySchedule[] = [];

                    while (current.isSameOrBefore(props.to, 'day')) {
                        const dateKey = current.format('YYYY-MM-DD');
                        if (!existingDates.has(dateKey)) {
                            newDays.push({
                                date: current.clone(),
                                enabled: false,
                                doses: medication.specificSchedule[0]?.doses || [getNextScheduleTime()]
                            });
                        }
                        current.add(1, 'day');
                    }

                    if (newDays.length > 0) {
                        return {
                            ...medication,
                            specificSchedule: [...filteredSchedule, ...newDays].sort((a, b) => a.date.diff(b.date))
                        };
                    }
                }
                return medication;
            });

            if (JSON.stringify(updatedMedications) !== JSON.stringify(medications)) {
                setMedications(updatedMedications);
            }
        }
    }, [props.from, props.to]);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SingleRescheduling:
            case NewBookingsStatus.SavingCreateAppointment:
                setSaving(true);
                break;
            case NewBookingsStatus.SingleRescheduleSuccess:
            case NewBookingsStatus.SavingCreateAppointmentSuccess:
            case NewBookingsStatus.Error:
                setSaving(false);
        }
    }, [newBookingsStatus]);

    return (
        <>
            <div className={styles.headerContainer}>
                <DefaultHeader
                    title="Feedings and Medications"
                    onBack={handleBack}
                />
            </div>
            <OverlayScrollbarsComponent style={{ height: '100%' }}>
                <div className={styles.content}>
                    <div className={styles.sectionContainer}>
                        <PetSearch
                            parentID={props.parentId}
                            petId={props.petId}
                            multiplePets={true}
                            readOnly={true}
                            hideSelect={true}
                            handlerPet={noop}
                        />
                    </div>
                    <div className={styles.sectionContainer}>
                        {feedings.map((feeding, index) => (
                            <Feedings
                                key={`feeding-${index}`}
                                feeding={feeding}
                                index={index}
                                disabled={disabledMoments}
                                optional={true}
                                isActive={isFeedingActive}
                                onUpdate={handleUpdateFeeding}
                                onDelete={handleDeleteFeeding}
                                onActivate={handleActivateFeeding}
                            />
                        ))}
                        {isFeedingActive && (
                            <button
                                className={styles.addButton}
                                onClick={handleAddFeeding}
                            >
                                + Add Feeding
                            </button>
                        )}
                    </div>
                    <div className={styles.sectionContainer}>
                        {medications.map((medication, index) => (
                            <Medications
                                key={`medication-${index}`}
                                medication={medication}
                                index={index}
                                from={medicationWindow.from}
                                to={medicationWindow.to}
                                flow={props.flow}
                                isActive={isMedicationActive}
                                onUpdate={handleUpdateMedication}
                                onDelete={handleDeleteMedication}
                                onActivate={handleActivateMedication}
                                onSetMedicationType={handleSetMedicationType}
                                onHasAlerts={hasAlerts => setMedicationAlerts(hasAlerts)}
                            />
                        ))}
                        {isMedicationActive && (
                            <button
                                className={styles.addButton}
                                onClick={handleAddMedication}
                            >
                                + Add Medication
                            </button>
                        )}
                    </div>
                </div>
            </OverlayScrollbarsComponent>
            <div className={styles.footerContainer}>
                <Button
                    className={styles.buttonSave}
                    disabled={disabledSave}
                    variant="green"
                    label="Save"
                    size="large"
                    isLoading={saveButtonLoading}
                    onClick={handleSave}
                />
            </div>
        </>
    );
};

export default FeedingsAndMedications;
